import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  setPersistence, 
  browserLocalPersistence,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode
} from 'firebase/auth';
import { getFirestore, collection, addDoc, doc, setDoc, getDoc, writeBatch } from 'firebase/firestore';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyAWagtsVI0M9foqwFVxt7hpUFkFDW_Tl9k",
  authDomain: "beckmann.equisuite.de",
  projectId: "ybis-6cb46",
  storageBucket: "ybis-6cb46.firebasestorage.app",
  messagingSenderId: "120784602870",
  appId: "1:120784602870:web:a4e1794043c28a4f90ced7",
  measurementId: "G-0FHVLTGCYM"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth with persistence
export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(console.error);

// Initialize Firestore
export const db = getFirestore(app);

// Export auth functions
export {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode
};

// Initialize Firebase Cloud Messaging
export let messaging: any = null;

// VAPID key for web push notifications
const VAPID_KEY = 'BAdjapIYdcIaRRMxzyCmBTMNT3wZaccKLYjjJ6d54QsjnsQmDjkwyuxC4LUBgwVH7j2c2lJRflbrrJeJvS3__7k';

// Initialize messaging if supported
const initializeMessaging = async () => {
  try {
    const isMessagingSupported = await isSupported();
    if (!isMessagingSupported) {
      console.log('Firebase Cloud Messaging is not supported');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service Workers are not supported');
      return false;
    }

    // Register service worker
    const swRegistration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none'
    });
    console.log('Service Worker registered:', swRegistration);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('Service Worker is ready');

    // Initialize messaging
    messaging = getMessaging(app);
    
    // Handle foreground messages
    onMessage(messaging, (payload) => {
      console.log('Received foreground message:', payload);
      if (Notification.permission === 'granted') {
        const notification = new Notification('Neue Benachrichtigung', {
          body: 'Sie haben eine neue Benachrichtigung erhalten',
          icon: '/icon-192x192.png',
          badge: '/badge-96x96.png',
          tag: 'notification',
          renotify: true,
          data: {
            url: '/dashboard/notifications'
          }
        });

        notification.onclick = () => {
          window.focus();
          window.location.href = '/dashboard/notifications';
        };
      }
    });

    return true;
  } catch (error) {
    console.error('Error initializing messaging:', error);
    return false;
  }
};

// Initialize notifications
export const initializeNotifications = async (userId: string) => {
  try {
    // Initialize messaging if not already initialized
    if (!messaging) {
      const initialized = await initializeMessaging();
      if (!initialized) {
        console.log('Failed to initialize messaging');
        return false;
      }
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return false;
    }

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.getRegistration('/sw.js')
    });

    if (!token) {
      console.error('No FCM token received');
      return false;
    }

    console.log('FCM token received:', token);

    // Save token to user's document
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      fcmToken: token,
      notificationsEnabled: true,
      notificationPreferences: {
        enabled: true,
        bookings: {
          newBookings: true,
          bookingConfirmations: true,
          bookingCancellations: true,
          bookingReminders: true,
          reminderHours: 24
        },
        horses: {
          coRiderRequests: true,
          coRiderUpdates: true,
          vetReminders: true,
          farrierReminders: true,
          reminderDays: 3
        },
        facilities: {
          maintenance: true,
          availability: true,
          blockedTimes: true
        },
        system: {
          announcements: true,
          featureUpdates: true,
          maintenanceMode: true
        }
      },
      lastLoginAt: new Date()
    }, { merge: true });

    console.log('FCM token saved to user document');
    return true;
  } catch (error) {
    console.error('Error initializing notifications:', error);
    return false;
  }
};

// Initialize collections and default settings
export const initializeCollections = async () => {
  try {
    const batch = writeBatch(db);

    // Initialize user permissions settings if they don't exist
    const settingsRef = doc(db, 'settings', 'userPermissions');
    const settingsDoc = await getDoc(settingsRef);
    if (!settingsDoc.exists()) {
      batch.set(settingsRef, {
        autoApproveUsers: false,
        excludedRoles: ['manager'],
        updatedAt: new Date()
      });
    }

    // Initialize notifications collection if it doesn't exist
    const notificationsRef = doc(db, 'settings', 'notifications');
    const notificationsDoc = await getDoc(notificationsRef);
    if (!notificationsDoc.exists()) {
      batch.set(notificationsRef, {
        initialized: true,
        updatedAt: new Date()
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error initializing collections:', error);
  }
};
